import { Intent } from "@blueprintjs/core";
import { Button } from "components/buttons";
import { DateToLocal } from "components/dates";
import { FormDateRangeInput } from "components/form-fields";
import { ListingTable } from "components/listing";
import { NotificationToaster } from "components/notifications";
import moment from "moment";
import { EmployeeLicenceCheckPreviousCheckModal } from "pages/employee/edit/licence-checking/shared";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { LicenceCheckingService } from "services";

export function EmployeeLicenceCheckHistory({
    employeeId,
    licenceCheckOptions = {}
}) {

    const [loadingData, setLoadingData] = useState(true);
    const tableHeaders = ["Type", "Detail", "Completed By", "Date", ""];
    const [tableData, setTableData] = useState([]);
    const [totalRecordCount, setTotalRecordCount] = useState(0);
    const [selectedCheck, setSelectedCheck] = useState(null);
    const [checkData, setCheckData] = useState([]);
    const [canGoToNextCheck, setCanGoToNextCheck] = useState(false);
    const [canGoToPrevCheck, setCanGoToPrevCheck] = useState(false);
    const badRequestCode = 400;
    const dateFormat = "YYYY-MM-DD";
    const recordsPerPage = 100;

    const [tableOptions, setTableOptions] = useState({
        startDate: moment().add(-1, 'y').format(dateFormat),
        endDate: moment().format(dateFormat),
        pageNumber: 1,
        pageSize: recordsPerPage,
        employeeId: null
    });


    useEffect(() => {

        setTableOptions((prev) => {
            return {
                ...prev,
                employeeId: employeeId
            }
        })

    }, [employeeId]);


    useEffect(() => {

        if (tableOptions.employeeId == null) {
            return;
        }

        setLoadingData(true);

        var errorHandling = function (error) {
            if (error.status === badRequestCode) {
                NotificationToaster.show(Intent.WARNING, error.data);
            } else {
                NotificationToaster.show(Intent.DANGER, "Failed to get credits history. Please refresh the page.");
            }
            setTableData([]);
            setTotalRecordCount(0);
        }

        LicenceCheckingService.getCreditsHistoryForEmployee(tableOptions.employeeId, tableOptions.pageSize, tableOptions.pageNumber, tableOptions.startDate, tableOptions.endDate).then(r => {

            let historyData = r.data.map((h) => {
                return (
                    [h.checkType,
                    h.checkTypeDetail,
                    h.completedBy,
                    <DateToLocal key={h.checkId} format="DD/MM/YYYY">{h.date ?? '-'}</DateToLocal>,
                    <Button key={h.checkId} text="View check" large={false} intent="primary" onClick={() => { onCheckClick(h) }} />
                    ]
                );
            });

            setCheckData(r.data);
            setTableData(historyData);
            setTotalRecordCount(r.totalCount);
        }, errorHandling).catch(errorHandling)
            .finally(() => {
                setLoadingData(false);
            });

    }, [tableOptions])

    function onDateSelect(dates) {

        if (dates.startDate.dateString != null && dates.endDate.dateString != null) {
            setTableOptions((prev) => {
                return {
                    ...prev,
                    startDate: dates.startDate.dateString,
                    endDate: dates.endDate.dateString
                }
            });
        }

    }

    function onTablePageChange(page) {
        setTableOptions((prev) => {
            return {
                ...prev,
                pageNumber: page,
            }
        });
    }

    function onCheckModalClose() {
        setSelectedCheck(null);
    }

    function onCheckClick(check) {
        setSelectedCheck(check);
    }

    function onNextCheckClick(checkId) {
        let foundCheck = checkData.findIndex((c) => c.checkId === checkId);
        foundCheck++;
        setSelectedCheck(checkData[foundCheck] ?? null);
    }

    function onPrevCheckClick(checkId) {
        let foundCheck = checkData.findIndex((c) => c.checkId === checkId);
        foundCheck--;
        setSelectedCheck(checkData[foundCheck] ?? null);
    }

    useEffect(function() {
        let foundCheck = checkData.findIndex((c) => c.checkId === selectedCheck?.checkId);
        setCanGoToNextCheck(foundCheck < checkData.length - 1);
        setCanGoToPrevCheck(foundCheck > 0);
    }, [checkData, selectedCheck]);

    return (
        <div>

            <FormDateRangeInput
                startDate={tableOptions.startDate}
                endDate={tableOptions.endDate}
                allowClear={false}
                onChange={onDateSelect}
                disabled={loadingData}
            />
            <ListingTable
                id="listing-table-licence-history"
                noDataMessage={"No checks for the selected date period"}
                sortable={false}
                headers={tableHeaders}
                data={tableData}
                totalRecordCount={totalRecordCount}
                pageable
                showPerPage={false}
                pageSize={recordsPerPage}
                loadingData={loadingData}
                onPagingChange={onTablePageChange}
                shrinkLastColumn
            />

            <EmployeeLicenceCheckPreviousCheckModal
                checkId={selectedCheck?.checkId}
                onModalClose={onCheckModalClose}
                employeeId={employeeId}
                licenceCheckOptions={licenceCheckOptions}
                historicCheckDetails={selectedCheck}
                onNextClick={onNextCheckClick}
                onPrevClick={onPrevCheckClick}
                canGoToNextCheck={canGoToNextCheck}
                canGoToPrevCheck={canGoToPrevCheck}
            />
        </div>
    );
}

EmployeeLicenceCheckHistory.propTypes = {
    employeeId: PropTypes.string.isRequired,
    licenceCheckOptions: PropTypes.object
};